import { showToast } from '@slices/apiSlice';
import { baseApi } from '@store/baseQuery';

import { authSuccess } from '../lib/authSlice';
import { emailSend } from '../lib/forgotPasswordSlice';
import {
    ForgotPasswordRequest,
    GeneralResponse,
    LoginRequest,
    MenteeAuthResponse,
    MenteeVerifyEmailResponse,
    MentorAuthResponse,
    MentorVerifyEmailResponse,
    ResetPasswordPayload,
    SignUpRequest,
} from '../types';

export const authApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        mentorLogin: builder.mutation<MentorAuthResponse, LoginRequest>({
            query: body => ({
                url: '/mentor/login',
                method: 'POST',
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            accessToken: data.accessToken,
                            refreshToken: data.refreshToken,
                            user: data.mentor,
                            isAuthenticated: true,
                            accessTokenExpiresIn: data.accessTokenExpiresIn,
                            refreshTokenExpiresIn: data.refreshTokenExpiresIn,
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        menteeLogin: builder.mutation<MenteeAuthResponse, LoginRequest>({
            query: body => ({
                url: '/mentee/login',
                method: 'POST',
                body,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            accessToken: data.accessToken,
                            refreshToken: data.refreshToken,
                            user: data.mentee,
                            isAuthenticated: true,
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        mentorSignup: builder.mutation<MentorAuthResponse, FormData>({
            query: body => ({
                url: '/mentor/register',
                method: 'POST',
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            accessToken: data.accessToken,
                            refreshToken: data.refreshToken,
                            user: data.mentor,
                            isAuthenticated: true,
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        menteeSignup: builder.mutation<MenteeAuthResponse, SignUpRequest>({
            query: body => ({
                url: '/mentee/register',
                method: 'POST',
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            accessToken: data.accessToken,
                            refreshToken: data.refreshToken,
                            user: data.mentee,
                            isAuthenticated: true,
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        mentorForgotPassword: builder.mutation<GeneralResponse, ForgotPasswordRequest>({
            query: body => ({
                url: '/mentor/forgot-password',
                method: 'POST',
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(emailSend({ isEmailSend: true }));
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        menteeForgotPassword: builder.mutation<GeneralResponse, ForgotPasswordRequest>({
            query: body => ({
                url: '/mentee/forgot-password',
                method: 'POST',
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(emailSend({ isEmailSend: true }));
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        mentorResetPassword: builder.mutation<GeneralResponse, ResetPasswordPayload>({
            query: body => ({
                url: '/mentor/reset-password',
                method: 'POST',
                body: { password: body.password },
                headers: { Authorization: `Bearer ${body.token}` },
            }),
        }),
        menteeResetPassword: builder.mutation<GeneralResponse, ResetPasswordPayload>({
            query: body => ({
                url: '/mentee/reset-password',
                method: 'POST',
                body: { password: body.password },
                headers: { Authorization: `Bearer ${body.token}` },
            }),
        }),
        mentorVerifyEmail: builder.mutation<MentorVerifyEmailResponse, { token: string }>({
            query: body => ({
                url: '/mentor/verify-email',
                method: 'POST',
                headers: { Authorization: `Bearer ${body.token}` },
            }),
        }),
        menteeVerifyEmail: builder.mutation<MenteeVerifyEmailResponse, { token: string }>({
            query: body => ({
                url: '/mentee/verify-email',
                method: 'POST',
                headers: { Authorization: `Bearer ${body.token}` },
            }),
        }),
        mentorReverifyEmail: builder.mutation<GeneralResponse, void>({
            query: () => ({
                url: '/mentor/reverify-email',
                method: 'POST',
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Verification email sent successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        menteeReverifyEmail: builder.mutation<GeneralResponse, void>({
            query: () => ({
                url: '/mentee/reverify-email',
                method: 'POST',
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Verification email sent successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
    }),
    overrideExisting: true,
});

export const {
    useMentorLoginMutation,
    useMentorForgotPasswordMutation,
    useMentorSignupMutation,
    useMentorResetPasswordMutation,
    useMentorVerifyEmailMutation,
    useMentorReverifyEmailMutation,
    useMenteeLoginMutation,
    useMenteeSignupMutation,
    useMenteeForgotPasswordMutation,
    useMenteeResetPasswordMutation,
    useMenteeVerifyEmailMutation,
    useMenteeReverifyEmailMutation,
} = authApi;
